/**
 * useCelebration — 4-tier celebration orchestration
 */

import { useState, useCallback, useRef } from 'react';
import { useSoundEffects, type SoundType } from './useSoundEffects';
import { useHaptic } from './useHaptic';
import type { MindoraMood } from '../components/character/MindoraCharacter';

export type CelebrationTier = 'task' | 'routine' | 'streak' | 'badge';

interface TierConfig {
  confettiCount: number;
  showFireworks: boolean;
  showScreenShake: boolean;
  characterMood: MindoraMood;
  haptic: 'tap' | 'success' | 'celebrate';
  sound: SoundType;
  duration: number;
}

const tierConfigs: Record<CelebrationTier, TierConfig> = {
  task: {
    confettiCount: 8,
    showFireworks: false,
    showScreenShake: false,
    characterMood: 'happy',
    haptic: 'tap',
    sound: 'taskChime',
    duration: 1000,
  },
  routine: {
    confettiCount: 50,
    showFireworks: false,
    showScreenShake: false,
    characterMood: 'celebrating',
    haptic: 'success',
    sound: 'routineJingle',
    duration: 2500,
  },
  streak: {
    confettiCount: 50,
    showFireworks: true,
    showScreenShake: true,
    characterMood: 'celebrating',
    haptic: 'celebrate',
    sound: 'streakFanfare',
    duration: 3000,
  },
  badge: {
    confettiCount: 30,
    showFireworks: false,
    showScreenShake: false,
    characterMood: 'celebrating',
    haptic: 'celebrate',
    sound: 'rewardSparkle',
    duration: 2000,
  },
};

export function useCelebration() {
  const [active, setActive] = useState(false);
  const [tier, setTier] = useState<CelebrationTier | null>(null);
  const [confettiCount, setConfettiCount] = useState(0);
  const [showFireworks, setShowFireworks] = useState(false);
  const [showScreenShake, setShowScreenShake] = useState(false);
  const [characterMood, setCharacterMood] = useState<MindoraMood>('idle');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { play } = useSoundEffects();
  const haptic = useHaptic();

  const celebrate = useCallback(
    (celebrationTier: CelebrationTier) => {
      const config = tierConfigs[celebrationTier];

      // Clear previous
      if (timerRef.current) clearTimeout(timerRef.current);

      setActive(true);
      setTier(celebrationTier);
      setConfettiCount(config.confettiCount);
      setShowFireworks(config.showFireworks);
      setShowScreenShake(config.showScreenShake);
      setCharacterMood(config.characterMood);

      // Fire haptic + sound
      haptic[config.haptic]();
      play(config.sound);

      // Auto-clear
      timerRef.current = setTimeout(() => {
        setActive(false);
        setTier(null);
        setConfettiCount(0);
        setShowFireworks(false);
        setShowScreenShake(false);
        setCharacterMood('idle');
      }, config.duration);
    },
    [haptic, play],
  );

  return {
    active,
    tier,
    confettiCount,
    showFireworks,
    showScreenShake,
    characterMood,
    celebrate,
  };
}
